import { motion } from "framer-motion";
import { ArrowRight, Gift, MapPin, ShieldCheck } from "lucide-react";
import { BUSINESS, isInSeason } from "@/data/services";

const GIFT_CARD_URL =
  "https://my.loopz.io/gift-card/7005b6c0-267f-485a-8dc7-667d3243ba5b?locale=en-CA";

const HERO_IMG = "/hero/thunderbird.webp";

const ACCENT_IMG = "/hero/owner.webp";

export default function Hero() {
  const inSeason = isInSeason(new Date());
  return (
    <section id="top" className="relative overflow-hidden pt-40 lg:pt-44 pb-16 lg:pb-24">
      {/* Ambient light */}
      <div className="absolute -top-40 -left-40 w-[700px] h-[700px] spotlight-amber pointer-events-none" />
      <div className="absolute top-1/3 -right-40 w-[600px] h-[600px] bg-[radial-gradient(circle,_rgba(227,6,19,0.08)_0%,_transparent_60%)] pointer-events-none" />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
          {/* LEFT — Copy */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="w-8 h-px bg-[#E30613]" />
              <span className="overline">Est. {BUSINESS.since} · {BUSINESS.base}</span>
            </motion.div>

            <h1
              className="font-display text-[42px] sm:text-6xl lg:text-[92px] leading-[0.92] tracking-tighter"
              data-testid="hero-headline"
            >
              Detailing{" "}
              <br />
              <span className="text-white/55">brought to your</span>
              <br />
              <span className="relative inline-block">
                doorstep
                <span className="absolute -right-4 -top-2 text-[#E30613]">.</span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3, ease: "easeOut" }}
              className="mt-8 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed"
            >
              Fully equipped mobile unit. We bring the water, power, and the wet-look
              finish — anywhere in the HRM. Your car, restored where it sits.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5, ease: "easeOut" }}
              className="mt-10 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-4"
            >
              <a
                href="#booking"
                data-testid="hero-book-btn"
                className="group inline-flex items-center gap-3 px-7 py-4 bg-[#E30613] text-white text-sm font-semibold rounded-sm hover:bg-[#B00510] active:scale-95 transition-[background-color,transform] duration-300 ease-out shadow-[0_0_40px_rgba(227,6,19,0.4)]"
              >
                Book your detail
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300 ease-out" />
              </a>
              <a
                href="#services"
                className="inline-flex items-center gap-2 px-6 py-4 border border-white/15 text-white/90 text-sm font-medium rounded-sm hover:bg-white/5 hover:border-white/30 transition-colors duration-300 ease-out"
                data-testid="hero-services-btn"
              >
                See packages
              </a>
              <a
                href={GIFT_CARD_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-giftcard-btn"
                className="group inline-flex items-center gap-2 px-6 py-4 border border-white/15 text-white/90 text-sm font-medium rounded-sm hover:bg-[#E30613]/10 hover:border-[#E30613] hover:text-[#E30613] transition-colors duration-300 ease-out"
              >
                <Gift className="w-4 h-4" />
                Gift Card
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-white/50"
            >
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#E30613]" />
                Fully Licensed & Insured
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#E30613]" />
                Serving {BUSINESS.area}
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`w-2 h-2 rounded-full ${
                    inSeason ? "bg-green-500 animate-pulse" : "bg-red-600"
                  }`}
                />
                {inSeason ? "Now booking for the season" : "Closed for the season"}
              </div>
            </motion.div>
          </div>

          {/* RIGHT — Cinematic image stack */}
          <div className="lg:col-span-5 relative h-[420px] sm:h-[520px] lg:h-[640px]">
            {/* Primary hero image = the LCP element. Rendered OUTSIDE Framer Motion
                so it's paint-visible immediately when the browser parses the DOM.
                Framer animations elsewhere in the hero still give plenty of
                cinematic feel without blocking Google's Core Web Vitals. */}
            <div className="absolute top-0 right-0 w-[78%] h-[70%] overflow-hidden rounded-sm border border-white/10">
              <img
                src={HERO_IMG}
                srcSet="/hero/thunderbird-800.webp 800w, /hero/thunderbird.webp 1400w"
                sizes="(max-width: 1023px) 90vw, 45vw"
                alt="Freshly detailed classic Ford Thunderbird — Darkside Detailing"
                className="w-full h-full object-cover"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.55, ease: "easeOut" }}
              className="absolute bottom-0 left-0 w-[62%] h-[52%] overflow-hidden rounded-sm border border-white/10 glow-amber"
            >
              <img
                src={ACCENT_IMG}
                alt="Darkside Detailing owner in front of a detailed black GMC"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.9 }}
              className="absolute -bottom-4 right-4 bg-[#0A0A0C]/80 backdrop-blur-xl border border-white/10 px-5 py-4 rounded-sm"
            >
              <div className="text-3xl font-display text-white">1500+</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-white/50 mt-1">
                Vehicles<br />detailed
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
